import {AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {ModalInterface} from '../../modal.interface';
import {defer, forkJoin, merge, Observable, of, Subject, Subscription, throwError} from 'rxjs';
import {ModalStateEvent} from '../../modal-state-event';
import {UntypedFormGroup} from '@angular/forms';
import {ProjectViewModel} from '../../../../features/user/garage/project.view-model';
import {ModalState} from '../../modal-state.enum';
import {DescriptionPostViewModel} from '../../../../features/user/project/about/details/description-post.view-model';
import {MediaObjectRestService} from '../../../../api/services/media-object.rest.service';
import {MediaObjectApiModel} from '../../../../api/models/media-object.api-model';
import {v4 as uuidv4} from 'uuid';
import {debounceTime, distinctUntilChanged, finalize, ignoreElements, mergeMap, switchMap, tap, takeUntil} from 'rxjs/operators';
import {EventApiModel} from '../../../../api/models/event.api.model';
import {CustomCameraMediaItem} from "../../../../features/custom-camera/custom-camera";
import {ModalComponent} from "../../modal.component";
import {TimelineComponent} from "../../../timeline/timeline.component";
import {CurrentUserViewModel} from "../../../../core/models/current-user.view-model";
import {TokenDataProviderService} from "../../../../core/services/token-data-provider.service";
import {Capacitor} from "@capacitor/core";
import {TranslateService} from "@ngx-translate/core";
import {UploadTaskTrackerService} from "../../../../core/services/upload-task-tracker.service";
import {UploadPreprocessService} from "../../../../core/services/upload-preprocess.service";
import {ResponseNotificationService} from "../../../../core/services/response-notification.service";
import {MediaPostPublishService} from "../../../../core/services/media-post-publish.service";
import {
  DEFAULT_MEDIA_FILTER_PRESET_ID,
  MEDIA_FILTER_PRESETS,
  MediaFilterPresetDefinition,
  MediaFilterPresetId,
  normalizeMediaFilterPresetId,
} from "../../../media/media-filter-presets";
import {
  hasRenderableMediaFilterEffects,
  renderCanvasWithMediaFilter,
} from "../../../media/media-filter-renderer";

@Component({
  selector: 'app-add-media',
  templateUrl: './add-media.component.html',
  styleUrls: ['./add-media.component.scss'],
})
export class AddMediaComponent implements ModalInterface, OnInit, AfterViewInit, OnDestroy {
  private static readonly MAX_VIDEO_SIZE_BYTES = 500 * 1024 * 1024;
  private static readonly MAX_IMAGE_SIZE_BYTES = 50 * 1024 * 1024;
  private static readonly MAX_VIDEO_DURATION_SECONDS = 60;
  public data: any;
  public src: string;
  public blob: Blob;
  public caption: string = null;
  public subject: Subject<ModalStateEvent>;
  public state: Observable<ModalStateEvent>;
  public form: UntypedFormGroup;
  @Input() public projectVM: ProjectViewModel;
  @Input() public eventAM: EventApiModel;
  @Input() public media: CustomCameraMediaItem;
  @Output() onAdd: EventEmitter<DescriptionPostViewModel> = new EventEmitter();
  public file?: File = null;
  public isImagePicked: boolean;
  public readonly isNative = Capacitor.isNativePlatform();
  public step: 'capture' | 'preview' | 'details' | 'thumbnail' = 'capture';
  public readonly filterPresets: MediaFilterPresetDefinition[] = MEDIA_FILTER_PRESETS;
  public selectedFilterPreset: MediaFilterPresetId = DEFAULT_MEDIA_FILTER_PRESET_ID;
  public filterSwatchDataUrls: Partial<Record<MediaFilterPresetId, string>> = {};
  public filteredImagePreviewDataUrl: string | null = null;
  public thumbnailDataUrl: string | null = null;
  public thumbnailBaseDataUrl: string | null = null;
  public thumbnailFile: File | null = null;
  public thumbnailTime = 0;
  public videoDuration = 0;
  public cameraInitialMode: 'photo' | 'story' = 'photo';
  private stateSubscription?: Subscription;
  private thumbnailTime$ = new Subject<number>();
  private thumbnailGenerationPromise: Promise<boolean> | null = null;
  private thumbnailRequestId = 0;
  private lastValidThumbnailTime = 0;
  private destroy$ = new Subject<void>();
  @ViewChild('thumbnailVideoEl') private thumbnailVideoEl?: ElementRef<HTMLVideoElement>;
  public thumbnailVideoSrc: string | null = null;
  public isPreparingPreview = false;
  public isVideoPreviewLoading = false;
  public isSubmitting = false;
  public currentUser: CurrentUserViewModel;
  private timeline: TimelineComponent;
  private thumbnailObjectUrl: string | null = null;
  private previewObjectUrl: string | null = null;
  private openedFromGallery = false;
  private submitSubscription?: Subscription;
  private publishDispatched = false;
  private previewAssetGenerationPromise: Promise<void> | null = null;
  private previewAssetGenerationId = 0;

  constructor(
    private mediaObjectRS: MediaObjectRestService,
    public readonly modalComponent: ModalComponent,
    private readonly tokenDPS: TokenDataProviderService,
    private readonly translate: TranslateService,
    private readonly uploadTaskTracker: UploadTaskTrackerService,
    private readonly uploadPreprocess: UploadPreprocessService,
    private readonly rns: ResponseNotificationService,
    private readonly mediaPostPublisher: MediaPostPublishService,
  ) {
  }

  async ngOnInit() {
    this.projectVM = this.data.projectVM;
    this.eventAM = this.data.eventAM;
    this.isImagePicked = this.data.isImagePicked;
    this.openedFromGallery = !!this.data.isImagePicked;
    this.timeline = this.data.timeline;
    this.tokenDPS.currentUser.subscribe((u: CurrentUserViewModel) => this.currentUser = u);

    if (this.isImagePicked) {
      await this.initResultStep(this.data.media);
    }

    this.thumbnailTime$
      .pipe(
        debounceTime(240),
        distinctUntilChanged(),
        switchMap(time => this.generateThumbnailAt(time)),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngAfterViewInit(): void {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stateSubscription?.unsubscribe();
    if (this.thumbnailObjectUrl) {
      URL.revokeObjectURL(this.thumbnailObjectUrl);
      this.thumbnailObjectUrl = null;
    }
    this.clearPreviewObjectUrl();
    this.removePreviewDialogClass();
    this.cancelInFlightSubmission(false);
  }

  async initResultStep(media: CustomCameraMediaItem): Promise<void> {
    this.stateSubscription?.unsubscribe();
    this.stateSubscription = this.state.subscribe((mse: ModalStateEvent) => {
      if (mse.status === ModalState.PENDING) {
        this.handleProceed().catch(() => {
          this.subject.next(new ModalStateEvent(ModalState.VALID));
        });
      }
    });

    this.uploadTaskTracker.clearTasksByFlow('media_objects.');
    this.modalComponent.hasButtons = true;
    this.isImagePicked = true;
    this.media = media;
    this.selectedFilterPreset = DEFAULT_MEDIA_FILTER_PRESET_ID;
    this.filteredImagePreviewDataUrl = null;
    this.filterSwatchDataUrls = {};
    this.thumbnailBaseDataUrl = null;
    this.previewAssetGenerationId += 1;
    this.clearPreviewObjectUrl();
    this.step = 'preview';
    this.isPreparingPreview = true;
    this.cameraInitialMode = media.type === 'video' ? 'story' : 'photo';
    this.updateModalButtons();
    this.syncModalLayout();
    this.thumbnailVideoSrc = media.type === 'video' ? this.getThumbnailSource(media) : null;
    this.isVideoPreviewLoading = media.type === 'video';
    this.thumbnailDataUrl = null;
    this.thumbnailFile = null;
    this.videoDuration = 0;
    this.thumbnailTime = 0;
    this.lastValidThumbnailTime = 0;
    const url = this.resolveFetchableUrl(this.media.src);
    try {
      const id = uuidv4();
      let sourceBlob = await this.readBlobFromSource(url);

      if ((!sourceBlob || sourceBlob.size === 0) && this.media.base64 && this.media.base64 !== url) {
        sourceBlob = await this.readBlobFromSource(this.media.base64);
      }

      if (!sourceBlob || sourceBlob.size === 0) {
        throw new Error('Unable to read selected media file (empty blob).');
      }

      const sourceType = sourceBlob.type || this.media.mimeType || this.detectMimeTypeFromMedia(this.media);
      const sourceFile = new File([sourceBlob], id, {type: sourceType, lastModified: Date.now()});
      if (!this.validateMediaFileSize(sourceFile)) {
        this.file = null;
        this.blob = null;
        this.subject.next(new ModalStateEvent(ModalState.INVALID));
        return;
      }
      if (!(await this.validateVideoDuration(sourceFile))) {
        this.file = null;
        this.blob = null;
        this.subject.next(new ModalStateEvent(ModalState.INVALID));
        return;
      }

      const previewCategory = this.media.type === 'video' ? 'video' : 'image';
      const previewPrepared = await this.uploadPreprocess.preprocessForPreview(sourceFile, previewCategory);

      this.file = previewPrepared.file;
      this.blob = previewPrepared.file;

      if (this.media.type === 'image') {
        this.clearPreviewObjectUrl();
        this.previewObjectUrl = URL.createObjectURL(this.blob);
        this.src = this.previewObjectUrl;
        void this.refreshPreviewDerivedAssets();
      } else {
        this.src = null;
      }

      if (this.media.type === 'video') {
        this.thumbnailVideoSrc = this.resolveFetchableUrlFromFile(this.file);
        this.isVideoPreviewLoading = true;
      }
    } catch (error) {
      console.error('[AddMedia] initResultStep failed', error);
      this.file = null;
      this.blob = null;
      this.subject.next(new ModalStateEvent(ModalState.INVALID));
      return;
    } finally {
      this.isPreparingPreview = false;
    }

    this.subject.next(new ModalStateEvent(ModalState.VALID));

    if (this.media.type === 'video') {
      this.ensureDefaultThumbnail()
        .then(() => this.refreshPreviewDerivedAssets())
        .catch(() => {});
    }
  }

  // private createForm() {
  //   // const validators: Validators[] = [RxwebValidators.required(), RxwebValidators.minLength({value: 3}), RxwebValidators.maxLength({value: 255})];
  //   this.form = this.fb.group(
  //     {
  //       file: [null, [RxwebValidators.minLength({value: 1})]],
  //       caption: [null, [RxwebValidators.minLength({value: 1})]],
  //     }
  //   );
  //
  //   // if (this.media) {
  //   //   this.form.get('content').setValue(this.media.content);
  //   // }
  //
  //   this.form.valueChanges.subscribe(() => {
  //     if (this.form.valid) {
  //       this.subject.next(new ModalStateEvent(ModalState.VALID));
  //     }
  //   })
  // }

  public submit(): void {
    if (this.isSubmitting) {
      return;
    }
    this.handleSubmit().catch(() => {
      this.isSubmitting = false;
      this.subject.next(new ModalStateEvent(ModalState.VALID));
    });
  }

  private async handleSubmit(): Promise<void> {
    if (this.isSubmitting || this.publishDispatched) {
      return;
    }
    await this.awaitPendingPreviewAssets();
    this.isSubmitting = true;
    this.updateModalButtons();
    if (!this.file) {
      this.file = await this.recoverFileFromCurrentMedia();
      if (!this.file) {
        console.warn('[AddMedia] submit blocked: missing media file.');
        this.isSubmitting = false;
        this.subject.next(new ModalStateEvent(ModalState.VALID));
        return;
      }
    }

    if (!this.validateMediaFileSize(this.file)) {
      this.isSubmitting = false;
      this.updateModalButtons();
      this.subject.next(new ModalStateEvent(ModalState.VALID));
      return;
    }
    if (!(await this.validateVideoDuration(this.file))) {
      this.isSubmitting = false;
      this.updateModalButtons();
      this.subject.next(new ModalStateEvent(ModalState.VALID));
      return;
    }

    if (this.media?.type === 'video' && (!this.thumbnailDataUrl || !this.thumbnailFile)) {
      await this.ensureThumbnailUpToDate();
    }
    const optimisticPost = await this.mediaPostPublisher.publish({
      file: this.file,
      caption: this.caption || '',
      thumbnailFile: this.thumbnailFile,
      thumbnailTime: this.thumbnailTime,
      filterPreset: this.getSelectedFilterPresetForUpload(),
      isVideo: this.isVideoFile(this.file),
      projectVM: this.projectVM,
      eventAM: this.eventAM,
      currentUser: this.currentUser,
      timeline: this.timeline,
      localThumbnailUri: this.getOptimisticThumbnailUri(),
    });
    if (!optimisticPost) {
      this.isSubmitting = false;
      this.updateModalButtons();
      this.subject.next(new ModalStateEvent(ModalState.VALID));
      return;
    }

    this.isSubmitting = false;
    this.updateModalButtons();
    this.subject.next(new ModalStateEvent(ModalState.SUCCESS, optimisticPost));
    this.publishDispatched = true;
  }

  public async handleProceed(): Promise<void> {
    if (this.isPreparingPreview || this.isSubmitting) {
      return;
    }
    if (this.step === 'preview') {
      await this.awaitPendingPreviewAssets();
      this.step = 'details';
      this.updateModalButtons();
      this.syncModalLayout();
      if (this.media?.type === 'video' && !this.thumbnailDataUrl) {
        await this.ensureDefaultThumbnail();
      }
      this.subject.next(new ModalStateEvent(ModalState.VALID));
      return;
    }
    if (this.step === 'thumbnail') {
      await this.ensureThumbnailUpToDate();
      this.step = 'details';
      this.updateModalButtons();
      this.syncModalLayout();
      this.subject.next(new ModalStateEvent(ModalState.VALID));
      return;
    }
    if (this.step === 'details') {
      this.submit();
    }
  }

  public openThumbnailPicker(): void {
    if (this.media?.type !== 'video') {
      return;
    }
    this.step = 'thumbnail';
    this.updateModalButtons();
    this.syncModalLayout();
    this.subject.next(new ModalStateEvent(ModalState.VALID));
    setTimeout(() => this.ensureDefaultThumbnail(), 0);
  }

  public cancelThumbnailPicker(): void {
    this.step = 'details';
    this.updateModalButtons();
    this.syncModalLayout();
    this.subject.next(new ModalStateEvent(ModalState.VALID));
  }

  public async onThumbnailTimeChange(value: number | string): Promise<void> {
    const parsed = typeof value === 'number' ? value : parseFloat(value);
    if (!Number.isFinite(parsed)) {
      return;
    }
    this.thumbnailTime = parsed;
    this.lastValidThumbnailTime = parsed;
    this.thumbnailTime$.next(parsed);
  }

  public selectFilterPreset(presetId: MediaFilterPresetId): void {
    const normalizedPresetId = normalizeMediaFilterPresetId(presetId);
    if (normalizedPresetId === this.selectedFilterPreset) {
      return;
    }

    this.selectedFilterPreset = normalizedPresetId;
    void this.refreshPreviewDerivedAssets();
  }

  public isFilterSelected(presetId: MediaFilterPresetId): boolean {
    return this.selectedFilterPreset === presetId;
  }

  public getCurrentImagePreviewSource(): string | null {
    return this.filteredImagePreviewDataUrl || this.previewObjectUrl || this.src || null;
  }

  public shouldForceFallbackVideoPreview(): boolean {
    return this.media?.type === 'video'
      && hasRenderableMediaFilterEffects(this.selectedFilterPreset);
  }

  public getFilterSwatchSource(presetId: MediaFilterPresetId): string | null {
    return this.filterSwatchDataUrls[presetId] || null;
  }

  private updateModalButtons(): void {
    if (this.step === 'capture') {
      this.modalComponent.isHeaderVisible = false;
      this.modalComponent.hasBackButton = false;
      this.modalComponent.hasDismissButton = false;
      this.modalComponent.hasProceedButton = false;
      this.modalComponent.disableBackButton = false;
      this.modalComponent.disableDismissButton = false;
      this.modalComponent.hasButtons = false;
      return;
    }

    if (this.step === 'preview') {
      const allowBackToCamera = !this.openedFromGallery;
      this.modalComponent.btnSuccessName = this.translate.instant('shared.modal.component.add_media.button.next');
      this.modalComponent.isHeaderVisible = false;
      this.modalComponent.hasButtons = true;
      this.modalComponent.hasBackButton = allowBackToCamera;
      this.modalComponent.disableBackButton = this.isSubmitting;
      this.modalComponent.backButtonLabel = this.translate.instant('common.button.back');
      this.modalComponent.backCallback = () => this.resetToCaptureStep();
      this.modalComponent.hasDismissButton = true;
      this.modalComponent.disableDismissButton = false;
      this.modalComponent.hasProceedButton = true;
      return;
    }
    if (this.step === 'thumbnail') {
      this.modalComponent.btnSuccessName = this.translate.instant('shared.modal.component.add_media.button.thumbnail_done');
      this.modalComponent.isHeaderVisible = true;
      this.modalComponent.hasBackButton = true;
      this.modalComponent.disableBackButton = this.isSubmitting;
      this.modalComponent.backButtonLabel = this.translate.instant('shared.modal.component.add_media.button.thumbnail_cancel');
      this.modalComponent.backCallback = () => this.cancelThumbnailPicker();
      this.modalComponent.hasDismissButton = false;
      this.modalComponent.disableDismissButton = false;
      this.modalComponent.hasProceedButton = true;
      return;
    }
    this.modalComponent.btnSuccessName = this.translate.instant('shared.modal.modal_component.button.proceed');
    this.modalComponent.isHeaderVisible = true;
    this.modalComponent.hasBackButton = true;
    this.modalComponent.disableBackButton = this.isSubmitting;
    this.modalComponent.backButtonLabel = this.translate.instant('common.button.back');
    this.modalComponent.backCallback = () => {
      this.step = 'preview';
      this.updateModalButtons();
      this.syncModalLayout();
      this.subject.next(new ModalStateEvent(ModalState.VALID));
    };
    this.modalComponent.hasDismissButton = true;
    this.modalComponent.disableDismissButton = false;
    this.modalComponent.hasProceedButton = true;
  }

  private syncModalLayout(): void {
    const dialog = document.querySelector('.app-media-modal.p-dialog') as HTMLElement | null;
    if (!dialog) {
      return;
    }
    if (this.step === 'preview') {
      dialog.classList.add('app-media-modal--preview');
    } else {
      dialog.classList.remove('app-media-modal--preview');
    }
  }

  private removePreviewDialogClass(): void {
    const dialog = document.querySelector('.app-media-modal.p-dialog') as HTMLElement | null;
    dialog?.classList.remove('app-media-modal--preview');
  }

  private resetToCaptureStep(): void {
    if (this.media?.type) {
      this.cameraInitialMode = this.media.type === 'video' ? 'story' : 'photo';
    }
    this.step = 'capture';
    this.isImagePicked = false;
    this.media = null;
    this.file = null;
    this.src = null;
    this.blob = null;
    this.isPreparingPreview = false;
    this.clearPreviewObjectUrl();
    this.selectedFilterPreset = DEFAULT_MEDIA_FILTER_PRESET_ID;
    this.filteredImagePreviewDataUrl = null;
    this.filterSwatchDataUrls = {};
    this.thumbnailBaseDataUrl = null;
    this.previewAssetGenerationId += 1;
    this.thumbnailDataUrl = null;
    this.thumbnailFile = null;
    this.thumbnailTime = 0;
    this.lastValidThumbnailTime = 0;
    this.videoDuration = 0;
    this.isVideoPreviewLoading = false;
    this.updateModalButtons();
    this.syncModalLayout();
    this.subject.next(new ModalStateEvent(ModalState.VALID));
  }

  private async ensureDefaultThumbnail(): Promise<void> {
    if (this.thumbnailDataUrl) {
      return;
    }
    await this.loadVideoMetadata();
    const defaultTime = this.videoDuration ? Math.min(1, this.videoDuration / 4) : 0;
    this.thumbnailTime = defaultTime;
    this.lastValidThumbnailTime = defaultTime;
    for (let i = 0; i < 6; i += 1) {
      const ok = await this.generateThumbnailAt(defaultTime);
      if (ok && this.thumbnailDataUrl) {
        return;
      }
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    }
  }

  private async loadVideoMetadata(): Promise<void> {
    if (this.videoDuration || !this.media) {
      return;
    }
    const video = await this.waitForThumbnailVideo();
    if (!video) {
      return;
    }
    video.preload = 'metadata';
    await new Promise<void>((resolve) => {
      const onMeta = () => {
        video.removeEventListener('loadedmetadata', onMeta);
        resolve();
      };
      const onError = () => {
        video.removeEventListener('error', onError);
        resolve();
      };
      video.addEventListener('loadedmetadata', onMeta);
      video.addEventListener('error', onError);
      video.load();
    });
    this.videoDuration = Number.isFinite(video.duration) ? video.duration : 0;
  }

  private async generateThumbnailAt(time: number): Promise<boolean> {
    if (!this.media) {
      return false;
    }
    const video = await this.waitForThumbnailVideo();
    if (!video) {
      return false;
    }
    const requestId = ++this.thumbnailRequestId;
    video.preload = 'auto';
    await new Promise<void>((resolve) => {
      const onLoaded = () => {
        window.clearTimeout(timeout);
        video.removeEventListener('loadeddata', onLoaded);
        video.removeEventListener('error', onError);
        resolve();
      };
      const onError = () => {
        window.clearTimeout(timeout);
        video.removeEventListener('loadeddata', onLoaded);
        video.removeEventListener('error', onError);
        resolve();
      };
      const timeout = window.setTimeout(() => {
        video.removeEventListener('loadeddata', onLoaded);
        video.removeEventListener('error', onError);
        resolve();
      }, 2000);

      if (video.readyState >= 2) {
        window.clearTimeout(timeout);
        resolve();
        return;
      }
      video.addEventListener('loadeddata', onLoaded);
      video.addEventListener('error', onError);
      video.load();
    });

    const safeTime = Math.max(0, Math.min(time, this.videoDuration || time));
    await new Promise<void>((resolve) => {
      const onSeeked = () => {
        video.removeEventListener('seeked', onSeeked);
        resolve();
      };
      video.addEventListener('seeked', onSeeked);
      try {
        video.currentTime = safeTime;
      } catch {
        video.removeEventListener('seeked', onSeeked);
        resolve();
      }
      window.setTimeout(() => {
        video.removeEventListener('seeked', onSeeked);
        resolve();
      }, 800);
    });

    const canvas = document.createElement('canvas');
    const width = video.videoWidth || 0;
    const height = video.videoHeight || 0;
    if (!width || !height) {
      return false;
    }
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(video, 0, 0, width, height);
      if (requestId === this.thumbnailRequestId) {
        this.thumbnailBaseDataUrl = canvas.toDataURL('image/jpeg', 0.85);
        const renderedCanvas = this.renderCanvasWithPreset(canvas, this.selectedFilterPreset);
        this.thumbnailDataUrl = renderedCanvas.toDataURL('image/jpeg', 0.85);
        this.thumbnailFile = await this.createThumbnailFile(renderedCanvas);
      }
    }
    return !!this.thumbnailDataUrl;
  }

  private async ensureThumbnailUpToDate(): Promise<void> {
    const latestTime = Number.isFinite(this.thumbnailTime)
      ? this.thumbnailTime
      : this.lastValidThumbnailTime;
    const generation = this.generateThumbnailAt(latestTime);
    const guardedGeneration = Promise.race<boolean>([
      generation,
      new Promise<boolean>((resolve) => window.setTimeout(() => resolve(false), 2500)),
    ]);
    this.thumbnailGenerationPromise = guardedGeneration;
    await guardedGeneration;
    if (this.thumbnailGenerationPromise === generation) {
      this.thumbnailGenerationPromise = null;
    }
    if (this.thumbnailGenerationPromise === guardedGeneration) {
      this.thumbnailGenerationPromise = null;
    }
  }

  private async createThumbnailFile(canvas: HTMLCanvasElement): Promise<File | null> {
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((result) => resolve(result), 'image/jpeg', 0.85)
    );
    if (!blob) {
      return null;
    }
    return new File([blob], `${uuidv4()}.jpg`, {type: 'image/jpeg', lastModified: Date.now()});
  }

  private getThumbnailSource(media: CustomCameraMediaItem): string {
    if (this.isNative && Capacitor.getPlatform() === 'android') {
      return this.resolveFetchableUrl(media.filePath);
    }
    return media.src;
  }

  private async waitForThumbnailVideo(): Promise<HTMLVideoElement | null> {
    for (let i = 0; i < 12; i += 1) {
      const video = this.thumbnailVideoEl?.nativeElement;
      if (video) {
        if (this.blob) {
          if (!this.thumbnailObjectUrl) {
            this.thumbnailObjectUrl = URL.createObjectURL(this.blob);
          }
          if (video.src !== this.thumbnailObjectUrl) {
            video.src = this.thumbnailObjectUrl;
          }
        } else if (!video.src && this.media) {
          video.src = this.getThumbnailSource(this.media);
        }
        return video;
      }
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    }
    return null;
  }

  addMedia(files: File[]): Observable<any> {
    // this.disabled = true;
    const requests: Observable<MediaObjectApiModel>[] = [];
    files.forEach((file: File) => {

        requests.push(this.postMediaObject(file));
      }
    );

    if (!requests.length) {
      return of([]);
    }

    return defer(() => {
      let counter = 0;
      const percent$ = new Subject();

      const modifiedObservablesList = requests.map(
        (item) => item.pipe(
          finalize(() => {
            const percentValue = ++counter * 100 / requests.length;
            percent$.next(percentValue);
          })
        )
      );

      const finalResult$ = forkJoin(modifiedObservablesList).pipe(
        tap(() => {
            percent$.next(100);
            percent$.complete();
          }
        ));

      return of([finalResult$, percent$.asObservable()]);
    }).pipe(
      mergeMap(([finalResult, progress]) => merge(
        progress.pipe(
          // tap((value) => this.mediaProgress = value),
          ignoreElements()
        ),
        finalResult
      ))
    );
  }

  public postMediaObject(file: File, caption: string = null): Observable<MediaObjectApiModel> {
    if (this.isVideoFile(file)) {
      return this.mediaObjectRS.uploadVideo(file, caption, this.thumbnailFile, this.thumbnailTime, this.getSelectedFilterPresetForUpload());
    }

    if (file?.type?.startsWith('image/')) {
      return this.mediaObjectRS.uploadImage(file, caption, this.getSelectedFilterPresetForUpload());
    }

    return throwError(() => new Error('Unsupported media type in add-media flow.'));
  }

  private resolveFetchableUrl(src?: string): string {
    if (!src) { return ''; }
    if (src.startsWith('data:')) { return src; }
    const converted = Capacitor.convertFileSrc(src);
    if (converted.startsWith('capacitor://localhost/')) {
      return converted.replace('capacitor://localhost/', 'http://localhost/');
    }
    return converted;
  }

  private resolveFetchableUrlFromFile(file: File): string {
    const objectUrl = URL.createObjectURL(file);
    if (this.thumbnailObjectUrl) {
      URL.revokeObjectURL(this.thumbnailObjectUrl);
    }
    this.thumbnailObjectUrl = objectUrl;
    return objectUrl;
  }

  private detectMimeTypeFromMedia(media: CustomCameraMediaItem): string {
    if (media?.type === 'video') {
      return 'video/mp4';
    }
    return 'image/jpeg';
  }

  private isVideoFile(file: File): boolean {
    if (file?.type?.startsWith('video/')) {
      return true;
    }
    const fallbackName = (file?.name || '').toLowerCase();
    return /\.(mp4|mov|m4v|avi|mkv|webm|3gp|hevc|heif)$/i.test(fallbackName);
  }

  private validateMediaFileSize(file: File): boolean {
    const isVideo = this.isVideoFile(file);
    const maxSize = isVideo ? AddMediaComponent.MAX_VIDEO_SIZE_BYTES : AddMediaComponent.MAX_IMAGE_SIZE_BYTES;

    if (file.size <= maxSize) {
      return true;
    }

    const messageKey = isVideo
      ? 'shared.modal.component.add_media.error.video_too_large'
      : 'shared.modal.component.add_media.error.image_too_large';
    this.rns.emitError(this.translate.instant(messageKey));
    return false;
  }

  private async validateVideoDuration(file: File): Promise<boolean> {
    if (!this.isVideoFile(file)) {
      return true;
    }

    const duration = await this.readVideoDuration(file);
    if (duration <= AddMediaComponent.MAX_VIDEO_DURATION_SECONDS + 0.01) {
      return true;
    }

    this.rns.emitError(this.translate.instant('shared.modal.component.add_media.error.video_too_long'));
    return false;
  }

  private async readVideoDuration(file: File): Promise<number> {
    return new Promise<number>((resolve) => {
      const video = document.createElement('video');
      const objectUrl = URL.createObjectURL(file);
      let done = false;

      const finish = (value: number) => {
        if (done) {
          return;
        }
        done = true;
        video.removeAttribute('src');
        try {
          video.load();
        } catch {
          // noop
        }
        URL.revokeObjectURL(objectUrl);
        resolve(value);
      };

      video.preload = 'metadata';
      video.onloadedmetadata = () => finish(Number(video.duration) || 0);
      video.onerror = () => finish(0);
      video.src = objectUrl;
    });
  }

  private clearPreviewObjectUrl(): void {
    if (this.previewObjectUrl) {
      URL.revokeObjectURL(this.previewObjectUrl);
      this.previewObjectUrl = null;
    }
  }

  private async recoverFileFromCurrentMedia(): Promise<File | null> {
    const candidates = [
      this.thumbnailVideoSrc,
      this.media?.src ? this.resolveFetchableUrl(this.media.src) : null,
      this.media?.base64 || null,
    ].filter((value): value is string => !!value);

    for (const candidate of candidates) {
      try {
        const blob = await this.readBlobFromSource(candidate);
        if (!blob || blob.size === 0) {
          continue;
        }
        const mimeType = blob.type || this.media?.mimeType || this.detectMimeTypeFromMedia(this.media);
        const extension = this.isVideoFile(new File([blob], 'tmp', { type: mimeType }))
          ? 'mp4'
          : 'jpg';
        this.blob = blob;
        return new File([blob], `${uuidv4()}.${extension}`, { type: mimeType, lastModified: Date.now() });
      } catch {
        // try next candidate
      }
    }

    return null;
  }

  private async readBlobFromSource(src: string): Promise<Blob> {
    if (!src) {
      throw new Error('Missing media source.');
    }

    if (src.startsWith('data:')) {
      return this.decodeDataUrlToBlob(src);
    }

    const response = await fetch(src);
    if (!response.ok) {
      throw new Error(`Unable to fetch source: ${response.status}`);
    }

    return response.blob();
  }

  private decodeDataUrlToBlob(dataUrl: string): Blob {
    const commaIndex = dataUrl.indexOf(',');
    if (commaIndex < 0) {
      throw new Error('Invalid data URL.');
    }

    const header = dataUrl.substring(0, commaIndex);
    const payload = dataUrl.substring(commaIndex + 1);
    const mimeMatch = header.match(/^data:([^;]+)(;base64)?/i);
    const mimeType = mimeMatch?.[1] || 'application/octet-stream';
    const isBase64 = /;base64/i.test(header);

    if (!isBase64) {
      return new Blob([decodeURIComponent(payload)], {type: mimeType});
    }

    const binary = atob(payload);
    const chunkSize = 1024;
    const chunks: Uint8Array[] = [];

    for (let offset = 0; offset < binary.length; offset += chunkSize) {
      const slice = binary.slice(offset, offset + chunkSize);
      const bytes = new Uint8Array(slice.length);
      for (let i = 0; i < slice.length; i += 1) {
        bytes[i] = slice.charCodeAt(i);
      }
      chunks.push(bytes);
    }

    return new Blob(chunks, {type: mimeType});
  }

  public onVideoPreviewLoadingChange(isLoading: boolean): void {
    this.isVideoPreviewLoading = isLoading;
  }

  public beforeModalCloseAttempt(): Promise<boolean> {
    if (this.isSubmitting) {
      this.cancelInFlightSubmission();
      return Promise.resolve(true);
    }

    // In Add Media flow "Dismiss" should immediately close and drop local draft state.

    if (this.step === 'capture') {
      return Promise.resolve(true);
    }

    return Promise.resolve(true);
  }

  private cancelInFlightSubmission(emitState: boolean = true): void {
    this.submitSubscription?.unsubscribe();
    this.submitSubscription = undefined;
    this.uploadPreprocess.cancelActivePreprocess();
    if (this.isSubmitting) {
      this.isSubmitting = false;
      this.updateModalButtons();
    }
    if (emitState) {
      this.subject.next(new ModalStateEvent(ModalState.VALID));
    }
  }

  private getOptimisticThumbnailUri(): string | null {
    if (this.media?.type === 'video') {
      return this.thumbnailDataUrl || null;
    }

    return this.filteredImagePreviewDataUrl || this.previewObjectUrl || this.src || null;
  }

  private getSelectedFilterPresetForUpload(): string | null {
    return this.selectedFilterPreset !== DEFAULT_MEDIA_FILTER_PRESET_ID
      ? this.selectedFilterPreset
      : null;
  }

  private async refreshPreviewDerivedAssets(): Promise<void> {
    const currentJob = (async () => {
      const generationId = ++this.previewAssetGenerationId;

      if (this.media?.type === 'image') {
        await Promise.all([
          this.refreshCurrentImagePreview(generationId),
          this.refreshFilterSwatches(generationId),
        ]);
        return;
      }

      if (this.media?.type === 'video' && (this.thumbnailBaseDataUrl || this.thumbnailDataUrl)) {
        await this.ensureThumbnailUpToDate();
        await this.refreshFilterSwatches(generationId);
      }
    })().catch((error) => {
      console.warn('[AddMedia] preview filter generation failed', error);
      if (this.media?.type === 'image') {
        this.filteredImagePreviewDataUrl = null;
      }
    });

    this.previewAssetGenerationPromise = currentJob;
    try {
      await currentJob;
    } finally {
      if (this.previewAssetGenerationPromise === currentJob) {
        this.previewAssetGenerationPromise = null;
      }
    }
  }

  private async awaitPendingPreviewAssets(): Promise<void> {
    if (!this.previewAssetGenerationPromise) {
      return;
    }

    await this.previewAssetGenerationPromise;
  }

  private async refreshCurrentImagePreview(generationId: number): Promise<void> {
    if (!this.file || !this.hasVisualFilterEffects(this.selectedFilterPreset)) {
      if (generationId === this.previewAssetGenerationId) {
        this.filteredImagePreviewDataUrl = null;
      }
      return;
    }

    const filteredPreviewDataUrl = await this.renderFilteredImagePreviewDataUrl(this.file, this.selectedFilterPreset);
    if (generationId === this.previewAssetGenerationId) {
      this.filteredImagePreviewDataUrl = filteredPreviewDataUrl;
    }
  }

  private async refreshFilterSwatches(generationId: number): Promise<void> {
    const source = this.media?.type === 'image'
      ? (this.previewObjectUrl || this.src || null)
      : (this.thumbnailBaseDataUrl || this.thumbnailDataUrl || null);

    if (!source) {
      if (generationId === this.previewAssetGenerationId) {
        this.filterSwatchDataUrls = {};
      }
      return;
    }

    const swatches = await this.renderFilterSwatches(source);
    if (generationId === this.previewAssetGenerationId) {
      this.filterSwatchDataUrls = swatches;
    }
  }

  private renderCanvasWithPreset(
    sourceCanvas: HTMLCanvasElement,
    presetId: MediaFilterPresetId,
  ): HTMLCanvasElement {
    if (!hasRenderableMediaFilterEffects(presetId)) {
      return sourceCanvas;
    }

    return renderCanvasWithMediaFilter(sourceCanvas, presetId);
  }

  private async renderFilteredImagePreviewDataUrl(file: File, presetId: MediaFilterPresetId): Promise<string | null> {
    const objectUrl = URL.createObjectURL(file);
    try {
      const image = await this.loadImageElement(objectUrl);
      const width = image.naturalWidth || image.width || 0;
      const height = image.naturalHeight || image.height || 0;
      if (!width || !height) {
        return null;
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d');
      if (!context) {
        return null;
      }

      context.drawImage(image, 0, 0, width, height);
      const renderedCanvas = this.renderCanvasWithPreset(canvas, presetId);
      return renderedCanvas.toDataURL('image/jpeg', 0.88);
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  }

  private async renderFilterSwatches(source: string): Promise<Partial<Record<MediaFilterPresetId, string>>> {
    const image = await this.loadImageElement(source);
    const swatches: Partial<Record<MediaFilterPresetId, string>> = {};

    this.filterPresets.forEach((preset) => {
      const baseCanvas = document.createElement('canvas');
      baseCanvas.width = 96;
      baseCanvas.height = 96;
      const baseContext = baseCanvas.getContext('2d');
      if (!baseContext) {
        return;
      }

      this.drawImageToCanvas(baseContext, image, 96, 96, 'cover');
      const renderedCanvas = this.renderCanvasWithPreset(baseCanvas, preset.id);
      swatches[preset.id] = renderedCanvas.toDataURL('image/jpeg', 0.82);
    });

    return swatches;
  }

  private hasVisualFilterEffects(presetId: MediaFilterPresetId): boolean {
    return hasRenderableMediaFilterEffects(presetId);
  }

  private drawImageToCanvas(
    context: CanvasRenderingContext2D,
    image: HTMLImageElement,
    width: number,
    height: number,
    mode: 'contain' | 'cover',
  ): void {
    const sourceWidth = image.naturalWidth || image.width || 0;
    const sourceHeight = image.naturalHeight || image.height || 0;
    if (!sourceWidth || !sourceHeight) {
      return;
    }

    const scale = mode === 'cover'
      ? Math.max(width / sourceWidth, height / sourceHeight)
      : Math.min(width / sourceWidth, height / sourceHeight);
    const drawWidth = sourceWidth * scale;
    const drawHeight = sourceHeight * scale;
    const offsetX = (width - drawWidth) / 2;
    const offsetY = (height - drawHeight) / 2;

    context.drawImage(image, offsetX, offsetY, drawWidth, drawHeight);
  }

  private async loadImageElement(src: string): Promise<HTMLImageElement> {
    return new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Unable to load media preview image.'));
      image.src = src;
    });
  }
}
